/**
 * Customer administration (sysadmin only): the companies the platform
 * closes the books for. Create, edit, deactivate (hidden from every
 * selector, nothing deleted) and reactivate. Every mutation refetches the
 * list and the selector.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/ui/PageHeader";
import { DataTable } from "../components/ui/DataTable";
import { EmptyState } from "../components/ui/EmptyState";
import { ErrorAlert } from "../components/ui/ErrorAlert";
import { useApiQuery } from "../hooks/useApiQuery";
import { useCustomer } from "../hooks/useCustomer";
import { useToast } from "../hooks/useToast";
import { api, ApiError, type AdminCustomer, type CustomerInput } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

const EMPTY: CustomerInput = {
  name: "",
  code: "",
  sap_company_code: "",
  tagetik_reporting_unit: "",
  currency: "",
  phone: "",
  email: "",
  address: "",
  notes: "",
};

export function AdminCustomersPage() {
  const { t } = useTranslation();
  usePageTitle(t("customers.title"));
  const toast = useToast();
  const qc = useQueryClient();
  const { refresh } = useCustomer();
  const query = useApiQuery(["admin", "customers"], api.adminCustomers);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ target: AdminCustomer | null } | null>(null);

  const rows = useMemo(() => query.data?.customers ?? [], [query.data]);

  const refetch = async () => {
    await qc.invalidateQueries({ queryKey: ["admin", "customers"] });
    await refresh();
  };

  async function run(id: string, fn: () => Promise<unknown>, ok: string) {
    setBusyId(id);
    try {
      await fn();
      toast(ok, "success");
      await refetch();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("admin.actionFailed"), "error");
    } finally {
      setBusyId(null);
    }
  }

  const columns = [
    { key: "name", label: t("customers.colName") },
    { key: "identity", label: t("customers.colIdentity") },
    { key: "contact", label: t("customers.colContact") },
    { key: "status", label: t("admin.colStatus") },
    { key: "actions", label: t("admin.colActions"), align: "right" as const },
  ];

  if (query.isError) {
    return <ErrorAlert error={query.error} onRetry={() => query.refetch()} />;
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        title={t("customers.title")}
        subtitle={t("customers.subtitle")}
        extra={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={() => setEditing({ target: null })}
          >
            {t("customers.create")}
          </Button>
        }
      />

      {rows.length === 0 && !query.isLoading ? (
        <EmptyState message={t("customers.empty")} />
      ) : (
        <DataTable columns={columns}>
          {rows.map((c) => {
            const busy = busyId === c.id;
            const active = c.status === "active";
            return (
              <TableRow key={c.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {c.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" className="num">
                    {c.code}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("customers.sapCompanyCode")}: {c.sap_company_code || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("customers.reportingUnit")}: {c.tagetik_reporting_unit || "—"} ·{" "}
                    {t("customers.currency")}: {c.currency || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {c.email || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {c.phone || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={active ? "success" : "default"}
                    label={t(`customers.status.${c.status}`, c.status)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title={t("customers.edit")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => setEditing({ target: c })}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {active ? (
                      <Tooltip title={t("customers.deactivate")}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={busy}
                            onClick={() =>
                              run(c.id, () => api.adminDeactivateCustomer(c.id), t("customers.deactivated"))
                            }
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("customers.activate")}>
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={busy}
                            onClick={() =>
                              run(c.id, () => api.adminActivateCustomer(c.id), t("customers.activated"))
                            }
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </DataTable>
      )}

      {editing && (
        <CustomerDialog
          target={editing.target}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            toast(t("customers.saved"), "success");
            await refetch();
          }}
        />
      )}
    </Stack>
  );
}

function CustomerDialog({
  target,
  onClose,
  onSaved,
}: {
  target: AdminCustomer | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<CustomerInput>(
    target
      ? {
          name: target.name,
          code: target.code,
          sap_company_code: target.sap_company_code,
          tagetik_reporting_unit: target.tagetik_reporting_unit,
          currency: target.currency,
          phone: target.phone,
          email: target.email,
          address: target.address,
          notes: target.notes,
        }
      : EMPTY,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (key: keyof CustomerInput) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  async function save() {
    setBusy(true);
    setError("");
    try {
      if (target) await api.adminUpdateCustomer(target.id, values);
      else await api.adminCreateCustomer(values);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("admin.actionFailed"));
    } finally {
      setBusy(false);
    }
  }

  const field = (key: keyof CustomerInput, label: string, extra: object = {}) => (
    <TextField
      label={label}
      value={values[key]}
      onChange={set(key)}
      fullWidth
      size="small"
      {...extra}
    />
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{target ? t("customers.editTitle") : t("customers.createTitle")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={8}>
            {field("name", t("customers.name"), { required: true, autoFocus: true })}
          </Grid>
          <Grid item xs={12} sm={4}>
            {field("code", t("customers.code"), { required: true, helperText: t("customers.codeHelp") })}
          </Grid>
          <Grid item xs={12} sm={4}>
            {field("sap_company_code", t("customers.sapCompanyCode"))}
          </Grid>
          <Grid item xs={12} sm={5}>
            {field("tagetik_reporting_unit", t("customers.reportingUnit"))}
          </Grid>
          <Grid item xs={12} sm={3}>
            {field("currency", t("customers.currency"))}
          </Grid>
          <Grid item xs={12} sm={6}>
            {field("phone", t("customers.phone"))}
          </Grid>
          <Grid item xs={12} sm={6}>
            {field("email", t("customers.email"), { type: "email" })}
          </Grid>
          <Grid item xs={12}>
            {field("address", t("customers.address"), { multiline: true, minRows: 2 })}
          </Grid>
          <Grid item xs={12}>
            {field("notes", t("customers.notes"), { multiline: true, minRows: 2 })}
          </Grid>
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={busy || !values.name.trim() || !values.code.trim()}
        >
          {t("customers.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
